import LoadingSpinner from "@/components/LoadingSpinner";
import TeamSelectDeprecated from "@/components/messaging/TeamSelect";
import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useState } from "react";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import ShopifyAccounts from "./ShopifyAccounts";
import { useNavigate } from "react-router-dom";
import IconArrowBack2 from "@/icons/IconArrowBack2";
import { IconClose1 } from "@velaro/velaro-component-lib";

interface ShopifyConfig {
  id: number;
  storeName: string;
  appClientId: string;
  appSecret: string;
  domain: string;
  teamId: number;
}

interface ShopifyStatus {
  connected: boolean;
  hasToken: boolean;
  storeName?: string;
  domain?: string;
}

const AI_TOOLS = [
  {
    name: "shopify_get_customer",
    label: "Look up customer",
    description: "Finds a customer by email or name and returns their account details, lifetime value, and order count."
  },
  {
    name: "shopify_search_products",
    label: "Search products",
    description: "Searches your product catalog by keyword and returns matching items with prices, availability, and URLs."
  },
  {
    name: "shopify_get_order",
    label: "Get order details",
    description: "Fetches a specific order by ID — line items, totals, fulfillment status, and tracking information."
  },
  {
    name: "shopify_get_recent_orders",
    label: "Recent orders for customer",
    description: "Returns the last 5 orders for a customer so the AI can answer 'where is my order?' without asking for an order number."
  }
];

export default function Shopify() {
  const api = useApi();
  const [configs, setConfigs] = useState<ShopifyConfig[]>([]);
  const { teamId } = useSelectedTeam();
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const [aiStatus, setAiStatus] = useState<ShopifyStatus | null>(null);
  const navigate = useNavigate();

  const fetchConfigs = useCallback(async () => {
    const resp = await api.messaging.get(`Shopify/Integrations/`);
    const data = await resp.json();
    setConfigs(Array.isArray(data) ? data : []);
    if (Array.isArray(data) && data.length === 1) {
      setSelectedConfigId(data[0].id);
    }
  }, [api.messaging]);

  const addConfig = useCallback(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Fetch AI status when a config is selected
  useEffect(() => {
    if (!selectedConfigId) {
      setAiStatus(null);
      return;
    }
    async function fetchStatus() {
      try {
        const resp = await api.messaging.get(`Shopify/OAuth/status/${selectedConfigId}`);
        if (resp.ok) {
          const data = await resp.json();
          setAiStatus(data);
        }
      } catch {
        // non-fatal — status panel shows unknown state
      }
    }
    fetchStatus();
  }, [api.messaging, selectedConfigId]);

  const deleteConfig = useCallback(
    async (id: number) => {
      setConfigs((oldState) => oldState.filter((x) => x.id !== id));
      await api.messaging.delete(`Shopify/Integrations/${id}`);
    },
    [api.messaging]
  );

  const handleConfigChange = (
    id: number,
    field: keyof ShopifyConfig,
    value: string
  ) => {
    setConfigs((prevConfigs) =>
      prevConfigs.map((config) =>
        config.id === id ? { ...config, [field]: value } : config
      )
    );
  };

  const aiActive = aiStatus?.hasToken === true;

  return (
    <div>
      <div className="mb-6">
        <ShopifyAccounts
          configs={configs}
          deleteConfig={deleteConfig}
          addConfig={addConfig}
          teamId={teamId!}
          onChange={handleConfigChange}
          onSelectConfig={setSelectedConfigId}
        />
      </div>

      {/* AI Tools panel — shown whenever a config is selected */}
      {selectedConfigId > 0 && (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
                AI Tools
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    aiActive
                      ? "bg-green-100 text-green-700"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {aiActive ? "Active" : aiStatus === null ? "Checking…" : "No token"}
                </span>
              </div>
              <div className="text-sm text-slate-500 mt-0.5">
                {aiActive
                  ? "These tools are available to any AI workflow node on this site. No field mapping required — the AI interprets Shopify data automatically."
                  : "Connect your Shopify store via OAuth to activate AI tools. Once connected, any AI workflow node can query your store in real time."}
              </div>
            </div>
            {aiActive && (
              <button
                type="button"
                onClick={() => navigate("/workflows/create")}
                className="text-sm px-4 py-2 bg-green-50 border border-green-300 text-green-700 rounded-lg hover:bg-green-100 whitespace-nowrap"
              >
                Create Shopify Workflow →
              </button>
            )}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
              Available tools (read-only, auto-activated)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {AI_TOOLS.map((tool) => (
                <div
                  key={tool.name}
                  className={`flex gap-3 p-3 rounded-lg border ${
                    aiActive
                      ? "border-green-200 bg-green-50"
                      : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div
                    className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${
                      aiActive ? "bg-green-500" : "bg-slate-300"
                    }`}
                  >
                    {aiActive && (
                      <svg viewBox="0 0 10 10" width="8" height="8" fill="none" aria-hidden="true">
                        <path d="M1.5 5L3.5 7L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700">{tool.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{tool.description}</div>
                    <code className="text-[10px] text-slate-400 mt-1 block">{tool.name}</code>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
              <strong>Why no field mapping?</strong> Field mapping is for the legacy agent-side CRM panel (searching and viewing Shopify records manually). These AI tools work differently — the AI calls Shopify GraphQL at runtime and uses language understanding to turn raw product/order/customer data into a natural response for the visitor. No configuration needed beyond connecting your store.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function BackToIntegrationLanding() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer"
      onClick={() => navigate("/integrations")}
    >
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
